import { useQuery } from '@tanstack/react-query';
import fetchProfitPoolData from 'components/pages/ProfitPoolOverview/apiUtils';
import { FilterContextData } from 'contexts/FilterContext';
import { useContext, useEffect, useState } from 'react';
import { StrategicOpportunityKpis } from '../StrategicOpportunity.types';
import { compact, sortBy } from 'lodash';

const useStrategicOpportunityData = (
    selectedStrategicOpportunityKpis: StrategicOpportunityKpis,
    selectedSubFilter: {
        dataKey: string;
        dimension: string;
        isAttribute: boolean;
    },
    selectedColoredBy: {
        key: string;
        dimension: string;
        isAttribute: boolean;
    },
) => {
    const [transformedData, setTransformedData] = useState<any[]>([]);
    const [allSortedTactics, setAllSortedTactics] = useState<string[]>([]);

    const {
        timePeriodSelection,
        idForRestOfComponent,
        allDimAndAttributeTypeList,
        timePeriodLoading,
        idForRestOfComponentLoading,
    } = useContext(FilterContextData);
    const isSelfColored =
        selectedColoredBy?.key === selectedSubFilter?.dataKey &&
        selectedColoredBy?.dimension === selectedSubFilter?.dimension;
    const parentSelection = isSelfColored ? { key: '', dimension: '' } : selectedColoredBy;

    const isQueryEnabled = Boolean(
        allDimAndAttributeTypeList &&
            'loading' in allDimAndAttributeTypeList &&
            allDimAndAttributeTypeList.loading === false &&
            !timePeriodLoading &&
            !idForRestOfComponentLoading &&
            timePeriodSelection,
    );

    const {
        data: xAxisData,
        isPending: isXAxisPending,
        isError: isXAxisError,
        isSuccess: isXAxisSuccess,
    } = useQuery({
        queryKey: [
            'strategic-opportunity-data',
            selectedStrategicOpportunityKpis.xaxis,
            selectedSubFilter,
            timePeriodSelection,
            idForRestOfComponent,
            parentSelection,
        ],
        queryFn: ({ signal }) => {
            return fetchProfitPoolData({
                selectedSubFilter,
                kpi: selectedStrategicOpportunityKpis.xaxis?.kpi,
                tierList: selectedStrategicOpportunityKpis.xaxis?.tier,
                yearAgo: false,
                groupedByList: ['attribute'],
                dimIdsFilter: idForRestOfComponent,
                frequency: 'M',
                timePeriod: JSON.parse(timePeriodSelection),
                signal,
                parentDimension: parentSelection.dimension,
                parentAttributeType: parentSelection.key,
            });
        },
        enabled: isQueryEnabled,
    });

    const {
        data: yAxisData,
        isPending: isYAxisPending,
        isError: isYAxisError,
        isSuccess: isYAxisSuccess,
    } = useQuery({
        queryKey: [
            'strategic-opportunity-data',
            selectedStrategicOpportunityKpis.yaxis,
            selectedSubFilter,
            timePeriodSelection,
            idForRestOfComponent,
            parentSelection,
        ],
        queryFn: ({ signal }) => {
            return fetchProfitPoolData({
                selectedSubFilter,
                kpi: selectedStrategicOpportunityKpis.yaxis?.kpi,
                tierList: selectedStrategicOpportunityKpis.yaxis?.tier,
                yearAgo: false,
                groupedByList: ['attribute'],
                dimIdsFilter: idForRestOfComponent,
                frequency: 'M',
                timePeriod: JSON.parse(timePeriodSelection),
                signal,
                parentDimension: parentSelection.dimension,
                parentAttributeType: parentSelection.key,
            });
        },
        enabled: isQueryEnabled,
    });

    const {
        data: bubbleSizeData,
        isPending: isBubbleSizePending,
        isError: isBubbleSizeError,
        isSuccess: isBubbleSizeSuccess,
    } = useQuery({
        queryKey: [
            'strategic-opportunity-data',
            selectedStrategicOpportunityKpis.bubbleSize,
            selectedSubFilter,
            timePeriodSelection,
            idForRestOfComponent,
        ],
        queryFn: ({ signal }) => {
            return fetchProfitPoolData({
                selectedSubFilter,
                kpi: selectedStrategicOpportunityKpis.bubbleSize?.kpi,
                tierList: selectedStrategicOpportunityKpis.bubbleSize?.tier,
                yearAgo: false,
                groupedByList: ['attribute'],
                dimIdsFilter: idForRestOfComponent,
                frequency: 'M',
                timePeriod: JSON.parse(timePeriodSelection),
                signal,
            });
        },
        enabled: isQueryEnabled,
    });

    useEffect(() => {
        // Only run data manipulation when all data is defined and has data arrays
        if (
            xAxisData?.data &&
            Array.isArray(xAxisData.data) &&
            yAxisData?.data &&
            Array.isArray(yAxisData.data) &&
            bubbleSizeData?.data &&
            Array.isArray(bubbleSizeData.data)
        ) {
            const isPercentMode =
                selectedStrategicOpportunityKpis.xaxis?.kpi === 'margin_percent' ||
                selectedStrategicOpportunityKpis.yaxis?.kpi === 'margin_percent';

            const getMapKey = (item: { attribute_ex_id?: string; attribute_desc?: string }) =>
                item.attribute_ex_id || item.attribute_desc || '';

            // Create a map to group data by attribute_ex_id (fallback to attribute_desc)
            const dataMap = new Map<
                string,
                {
                    yFieldValue?: number;
                    xFieldValue?: number;
                    bubbleSizeValue?: number;
                    attribute_desc: string;
                    attribute_ex_id?: string;
                    parentAttributeDesc?: string;
                    hasXRecord?: boolean;
                    hasYRecord?: boolean;
                }
            >();

            // Helper function to map KPI name from request to response key
            const getKpiResponseKey = (kpi: string | undefined): string => {
                if (!kpi) return '';
                // API request uses 'margin_percent' but response has 'margin_percentage'
                if (kpi === 'margin_percent') {
                    return 'margin_percentage';
                }
                return kpi;
            };

            const getKpiValue = (kpiValues: Record<string, number> | undefined, kpi: string | undefined) => {
                const responseKpiKey = getKpiResponseKey(kpi);
                const value = kpiValues?.[responseKpiKey];
                if (value != null) {
                    return value;
                }
                return isPercentMode ? 0 : undefined;
            };

            // Process xAxisData
            xAxisData.data.forEach((item: any) => {
                const mapKey = getMapKey(item);
                if (!mapKey) return;

                if (!dataMap.has(mapKey)) {
                    dataMap.set(mapKey, {
                        attribute_desc: item.attribute_desc,
                        attribute_ex_id: item.attribute_ex_id,
                    });
                }
                const entry = dataMap.get(mapKey)!;
                entry.hasXRecord = true;
                entry.xFieldValue = getKpiValue(item.kpi_values, selectedStrategicOpportunityKpis.xaxis?.kpi);
                entry.parentAttributeDesc = item.parent_attribute_desc;
                if (item.attribute_desc) {
                    entry.attribute_desc = item.attribute_desc;
                }
                if (item.attribute_ex_id) {
                    entry.attribute_ex_id = item.attribute_ex_id;
                }
            });

            // Process yAxisData
            yAxisData.data.forEach((item: any) => {
                const mapKey = getMapKey(item);
                if (!mapKey) return;

                if (!dataMap.has(mapKey)) {
                    dataMap.set(mapKey, {
                        attribute_desc: item.attribute_desc,
                        attribute_ex_id: item.attribute_ex_id,
                    });
                }
                const entry = dataMap.get(mapKey)!;
                entry.hasYRecord = true;
                entry.yFieldValue = getKpiValue(item.kpi_values, selectedStrategicOpportunityKpis.yaxis?.kpi);
                if (item.attribute_desc) {
                    entry.attribute_desc = item.attribute_desc;
                }
                if (item.attribute_ex_id) {
                    entry.attribute_ex_id = item.attribute_ex_id;
                }
            });

            // Process bubbleSizeData (bubbleSizeValue/volume_sales_equivalent values)
            bubbleSizeData.data.forEach((item: any) => {
                const mapKey = getMapKey(item);
                if (!mapKey) return;

                if (!dataMap.has(mapKey)) {
                    dataMap.set(mapKey, {
                        attribute_desc: item.attribute_desc,
                        attribute_ex_id: item.attribute_ex_id,
                    });
                }
                const entry = dataMap.get(mapKey)!;
                entry.bubbleSizeValue = item.kpi_values?.[selectedStrategicOpportunityKpis.bubbleSize?.kpi ?? ''];
            });

            // Collect all unique tactics from the full dataset (before filtering)
            // so that color mapping remains stable even when data points are filtered out
            const allTacticsSet = new Set<string>();
            Array.from(dataMap.values()).forEach((item) => {
                const tactic =
                    item.yFieldValue != null && item.xFieldValue != null
                        ? (item.parentAttributeDesc ?? item.attribute_desc)
                        : '';
                if (tactic) allTacticsSet.add(tactic);
            });
            const sortedAllTactics = Array.from(allTacticsSet).sort((a, b) =>
                a.localeCompare(b, undefined, { sensitivity: 'base' }),
            );

            // Transform to reducedOpprData format
            const transformed = Array.from(dataMap.values()).map((item) => {
                const hasAnyAxisRecord = item.hasXRecord || item.hasYRecord;
                if (!hasAnyAxisRecord) {
                    return;
                }

                let xFieldValue = item.xFieldValue;
                let yFieldValue = item.yFieldValue;

                if (isPercentMode) {
                    xFieldValue = item.hasXRecord ? (xFieldValue ?? 0) : 0;
                    yFieldValue = item.hasYRecord ? (yFieldValue ?? 0) : 0;
                } else if (xFieldValue == null || yFieldValue == null || !item.hasXRecord || !item.hasYRecord) {
                    return;
                }

                return {
                    tactics: item.parentAttributeDesc ?? item.attribute_desc,
                    name: item.attribute_desc,
                    attribute_ex_id: item.attribute_ex_id ?? '',
                    yFieldValue,
                    xFieldValue,
                    bubbleSizeValue: item.bubbleSizeValue ?? null,
                    parentAttributeDesc: item.parentAttributeDesc ?? null,
                };
            });
            // Sort case-insensitively by tactics for consistent ordering
            const finalTransformed = sortBy(compact(transformed), [(item: any) => item.tactics?.toLowerCase()]);
            setTransformedData(finalTransformed);
            setAllSortedTactics(sortedAllTactics);
        } else {
            setTransformedData([]);
        }
    }, [xAxisData, yAxisData, bubbleSizeData, selectedStrategicOpportunityKpis]);

    return {
        xAxisData,
        yAxisData,
        bubbleSizeData,
        transformedData,
        allSortedTactics,
        isPending: isXAxisPending || isYAxisPending || isBubbleSizePending || idForRestOfComponentLoading,
        isError: isXAxisError || isYAxisError || isBubbleSizeError,
        isSuccess: isXAxisSuccess || isYAxisSuccess || isBubbleSizeSuccess,
    };
};

export default useStrategicOpportunityData;
